/**
 * Form — AEM form component: modifies action URL for AEM formpost handler and
 * tracks submit-button clicks via UDL events.
*
 */
import { FormFragmentManager } from '@components/form/FormFragmentManager';
import { mapExtensionToType } from '@utils/fileTypes';
import { handleCustomUDL } from '@utils/UDLUtils';

export class Form {
  private ctx: HTMLElement;
  private abortController: AbortController;

  constructor(el: HTMLElement) {
    this.ctx = el;
    this.abortController = new AbortController();
  }

  init(): this {
    this.addFormSelector();
    this.initFragments();
    this.bindEvents();
    return this;
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Rewrites the AEM form action URL to route through the formpost handler.
   * e.g. `/content/foo.html` → `/content/foo.formpost.html`
   */
  private addFormSelector(): void {
    const form = this.ctx.querySelector('form');
    if (!form) return;
    const action = form.getAttribute('action');
    if (action && !action.includes('.formpost.')) {
      form.setAttribute(
        'action',
        action.replace(/(^[^#?]*)(\.html)($|\?.*$|#.*$)/, '$1.formpost.html$3'),
      );
    }
  }

  /**
   * Initialises form fragments (captcha refresh, field validation helpers, etc.).
   */
  private initFragments(): void {
    new FormFragmentManager(this.ctx).init();
  }

  private bindEvents(): void {
    const { signal } = this.abortController;
    const btn = this.ctx.querySelector<HTMLButtonElement>('button[type="submit"], .comp-button button');
    btn?.addEventListener('click', () => this.handleSubmitClick(), { signal });
  }

  private handleSubmitClick(): void {
    const isOnlineVerification = !!this.ctx.closest('.comp-online-verification');
    const isDownloadList = !!this.ctx.closest('.comp-downloadlist');

    if (isOnlineVerification) {
      const partNumber =
        this.ctx.querySelector<HTMLInputElement>('input[name="ref"]')?.value ?? null;
      const lotNumber =
        this.ctx.querySelector<HTMLInputElement>('input[name="lot"]')?.value ?? null;
      handleCustomUDL('online_verification', { part_number: partNumber, lot_number: lotNumber });
    } else if (isDownloadList) {
      const checkedInputs = Array.from(
        this.ctx.querySelectorAll<HTMLInputElement>('.checkbox-options'),
      ).filter((input) => input.checked);

      const downloads = checkedInputs.map((input) => {
        const container = input.closest<HTMLElement>('.teaser-entry');
        const link = container?.querySelector<HTMLAnchorElement>('.entry-link');
        const title = link?.getAttribute('data-resource-tracking-title') ?? '';
        const name = link?.getAttribute('data-resource-path')?.split('/').pop() ?? '';
        const extension = name.split('.').pop()?.toLowerCase() ?? '';
        return {
          link_url: link?.href,
          link_text: title,
          file_type: mapExtensionToType(extension),
          file_name: name,
        };
      });

      handleCustomUDL('download_asset_list_click', {
        downloads,
        selected_count: downloads.length,
      });
    } else {
      const formFields = this.ctx.querySelector<HTMLElement>('.form-fields');
      const formFieldsData: Record<string, string> = formFields
        ? { ...formFields.dataset }
        : {};
      const formName = this.ctx.querySelector('form')?.getAttribute('name') ?? '';

      handleCustomUDL('form_submit', {
        ...formFieldsData,
        form_name: formName,
        form_type: 'info_request',
      });
    }
  }

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="form"]')
      .forEach((el) => new Form(el).init());
  }
}
